import angular from 'angular';
import _ from 'lodash-es';
import kformUtils from '~/components/kform/utils/kform-utils';
import kformBuilderUtils from '~/components/kform-builder/utils/kform-builder.utils';
import templateModalConfirmRemove from '../modal/confirm-remove/modal-kform-builder-confirm-remove.html';
import ModalKformBuilderConfirmRemoveController
	from '../modal/confirm-remove/modal-kform-builder-confirm-remove.controller';
import templateModalComponentSettings
	from '../modal/component-settings/modal-kform-builder-component-settings.html';
import ModalKformBuilderComponentSettingsController
	from '../modal/component-settings/modal-kform-builder-component-settings.controller';

/**
 * @param {import('angular').IFilterService} $filter
 * @param {any} $scope
 * @param {any} $rootScope
 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
 * @param {import('angular').ITimeoutService} $timeout
 * @param {import('angular').translate.ITranslateService} $translate
 * @param {any} dndDragIframeWorkaround
 * @param {any} KformComponents
 * @param {any} Notification
 * @param {{items: any[]}} componentClipboard
 */
const KformBuilderListController = /* @ngInject */ (
	$filter,
	$scope,
	$rootScope,
	$uibModal,
	$timeout,
	$translate,
	dndDragIframeWorkaround,
	KformComponents,
	Notification,
	componentClipboard,
) => {
	$scope.builder = true;
	$rootScope.builder = true;
	$scope.hideCount = (_.isNumber($scope.hideDndBoxCount) ? $scope.hideDndBoxCount : 1);
	$scope.$watch('hideDndBoxCount', (/** @type {number} */ hideCount) => {
		$scope.hideCount = hideCount || 1;
	});

	$scope.formComponents = KformComponents.components;
	if (!$scope.component) {
		$scope.component = $scope.form;
		$scope.$watch('form', (/** @type {any} */ newForm) => {
			if ($scope.component.type === 'form') {
				$scope.component = newForm;
			}
		});
	}

	// Components depend on this existing
	if (!$scope.data) {
		$scope.data = {};
	}

	$scope.emit = (/** @type {any[]} */ ...arguments__) => {
		/**
		 * @type {any[]}
		 */
		const arguments2 = [].slice.call(arguments__);
		arguments2[0] = `formBuilder:${arguments2[0]}`;
		// eslint-disable-next-line prefer-spread
		$scope.$emit.apply($scope, arguments2);
	};

	$scope.$on('editComponent', async (/** @type {Event} */ event, /** @type {any} */ component) => {
		await $scope.editComponent(component);
	});

	$scope.$on('fbDragDrop', (/** @type {Event} */ event, /** @type {any} */ component) => {
		component.settings.id = Math.random().toString(36).slice(7);
		component.settings.overlay = {
			page: '1',
			top:  component.fbDropY,
			left: component.fbDropX,
		};
		$scope.addComponent(component.settings);
	});

	$scope.addComponent = (/** @type {any} */ component, /** @type {number | undefined} */ index = undefined) => {
		// Allow changing default lock options.
		if ($scope.options && $scope.options.noLockKeys) {
			delete component.source;
			delete component.lockKey;
		}

		// Add parent key to default key if parent is present.
		// Sometimes $scope.component is the parent but columns and tables it is actually the column.
		const parent = $scope.parent || $scope.component;
		if (
			parent.type !== 'form'
			&& parent.type !== 'resource'
			&& component.isNew
			&& component.key
			&& parent.key !== undefined
		) {
			$scope.parentKey = parent.key;
			component.key = $scope.parentKey + _.upperFirst(component.key);
		}
		else {
			$scope.parentKey = '';
		}

		if (index === undefined) {
			// eslint-disable-next-line no-param-reassign
			index = -1;
		}

		// If this is a root component and the display is a wizard, then we know
		// that they dropped the component outside of where it is supposed to go...
		// Instead append or prepend to the components array.
		if ($scope.component.display === 'wizard') {
			const pageIndex = (index === 0) ? 0 : $scope.form.components[$scope.form.page].components.length;
			// eslint-disable-next-line no-param-reassign
			index = pageIndex;
			$scope.$apply(() => {
				$scope.form.components[$scope.form.page].components.splice(pageIndex, 0, component);
			});
		}

		// Only edit immediately for components that are not resource comps.
		if (component.isNew && !component.lockConfiguration && (!component.key || (!component.key.includes('.')))) {
			$scope.editComponent(component, index);
		}
		else {
			// Ensure the component has a key.
			component.key = component.key || component.label || 'component';

			kformBuilderUtils.uniquify($scope.form, component);

			// Update the component to not be flagged as new anymore.
			kformUtils.eachComponent([component], (/** @type {any} */ child) => {
				delete child.isNew;
			}, true);
		}

		dndDragIframeWorkaround.isDragging = false;
		$scope.emit('add');

		// Make sure that they don't ever add a component on the bottom of the submit button.
		const lastComponent = $scope.component.components.at(-1);
		if (
			(lastComponent)
			&& (lastComponent.type === 'button')
			&& (lastComponent.action === 'submit')
		) {
			// There is only one element on the page.
			if ($scope.component.components.length === 1) {
				// eslint-disable-next-line no-param-reassign
				index = 0;
			}
			else if (index !== undefined && index >= $scope.component.components.length) {
				// eslint-disable-next-line no-param-reassign
				index -= 1;
			}
		}

		if (index === undefined) {
			// eslint-disable-next-line no-param-reassign
			index = $scope.component.components.length;
		}

		// Fix path
		if (component.kdata?.path?.length === 0) {
			component.kdata.path.push({
				kid:        $scope.options.class_kid,
				linker_kid: '0',
			});
		}

		// Add the component to the components array.
		$scope.component.components.splice(index, 0, component);
		$timeout($scope.$apply.bind($scope));

		// Return true since this will tell the drag-and-drop list component to not insert into its own array.
		return true;
	};

	$scope.$on('updateKey', (/** @type {any} */ component) => $scope.updateKey(component));

	$scope.updateKey = (/** @type {any} */ component) => {
		if (!component.lockKey && component.isNew) {
			if (Object.prototype.hasOwnProperty.call($scope.data, component.key)) {
				delete $scope.data[component.key];
			}
			if (component.label) {
				const invalidRegex = /^[^A-Z_a-z]*|[^\w-]*/g;
				component.key = _.camelCase(`${$scope.parentKey} ${component.label.replaceAll(invalidRegex, '')}`);
			}
			kformBuilderUtils.uniquify($scope.form, component);
			$scope.data[component.key] = component.multiple ? [''] : '';
		}
	};

	const remove = (/** @type {any} */ component) => {
		if ($scope.component.components.includes(component)) {
			$scope.component.components.splice($scope.component.components.indexOf(component), 1);
			$scope.emit('remove', component);
		}
	};

	$scope.saveComponent = (/** @type {any} */ component) => {
		$scope.emit('update', component);
		// ngDialog.closeAll(true);
	};

	$scope.removeComponent = (/** @type {any} */ component, /** @type {boolean} */ shouldConfirm) => {
		if (shouldConfirm) {
			$uibModal.open({
				ariaLabelledBy:  'modal-kform-builder-confirm-remove-title',
				ariaDescribedBy: 'modal-kform-builder-confirm-remove-body',
				template:        templateModalConfirmRemove,
				controller:      ModalKformBuilderConfirmRemoveController,
				controllerAs:    '$ctrl',
				size:            'sm',
				backdrop:        'static',
			}).result.then(confirm => {
				if (confirm) {
					remove(component);
				}
				return true;
			}).catch(() => {});
		}
		else {
			remove(component);
		}
	};

	// Edit a specific component.
	$scope.editComponent = async (/** @type {any} */ component, index = 0) => {
		$scope.formComponent = KformComponents.components[component.type] || KformComponents.components.custom;
		// No edit view available
		if (!Object.prototype.hasOwnProperty.call($scope.formComponent, 'views')) {
			return;
		}

		/** @type {any} */
		const resolve = {
			classKid:  () => $scope.options.class_kid,
			parent:    () => $scope,
			component: () => component,
			data:      {},
			form:      $scope.form,
			index,
		};

		if (component.key) {
			resolve.data[component.key] = component.multiple ? [''] : '';
		}

		const previousSettings = angular.copy(component);

		// Make sure this component has a key.
		if (!component.key) {
			component.key = component.type;
		}

		// Open the dialog.
		$scope.updateKey(component);
		const originalKey = component.key;
		try {
			const save = await $uibModal.open({
				ariaLabelledBy:  'modal-kform-builder-component-settings-title',
				ariaDescribedBy: 'modal-kform-builder-component-settings-body',
				template:        templateModalComponentSettings,
				controller:      ModalKformBuilderComponentSettingsController,
				resolve,
				size:            'full-with-footer',
			}).result;

			if (save) {
				// If there is no component label, then set it to the key and set hide label to ensure reverse compatibility.
				// Don't calculate for components that don't have a label.
				if (!component.label && ![
					'panel',
					'content',
					'fieldset',
					'table',
					'well',
					'columns',
				].includes(component.type)) {
					component.key = originalKey;
					component.label = component.key || component.type;
					component.hideLabel = true;
				}

				kformUtils.eachComponent([component], (/** @type {any} */ child) => {
					delete child.isNew;
				}, true);

				$scope.emit('edit', component);
			}
			else {
				if (component.isNew) {
					remove(component);
				}

				// Revert to old settings, but use the same object reference
				_.assign(component, previousSettings);
			}
		}
		catch (error) {
			if (error !== 'backdrop click') {
				console.error(error);
			}
		}
	};

	// Clone form component
	$scope.cloneComponent = (/** @type {any} */ component) => {
		const newComponent = angular.copy(component);

		// If we are in a panel, the cloned component container should be the parent.components,
		// Otherwise it should be the form.components.
		let container;
		if ($scope.$parent.component && $scope.$parent.component.type === 'columns') {
			const column = _.find(
				$scope.$parent.component.columns,
				column2 => _.find(column2.components, { key: newComponent.key }),
			);
			container = column.components;
		}
		else if ($scope.$parent.component) {
			container = $scope.$parent.component.components;
		}
		else {
			container = $scope.form.components;
		}

		// Add the new component right after the original.
		const index = container.indexOf(component);
		container.splice(index + 1, 0, newComponent);

		// timeout: wait for the form to have the new component.
		$timeout(() => {
			kformUtils.eachComponent([newComponent], (/** @type {any} */ child) => {
				child.isNew = true;
				kformBuilderUtils.uniquify($scope.form, child);
				delete child.isNew;
			}, true);
		});
	};

	$scope.copyComponent = (/** @type {any} */ component) => {
		componentClipboard.items.push(angular.copy(component));
		Notification.info({
			title:   $translate.instant('common.info'),
			message: $filter('sprintf')($translate.instant('mf.has-been-copied'), component.label),
		});
	};

	// Add to scope so it can be used in templates
	$scope.dndDragIframeWorkaround = dndDragIframeWorkaround;
};

export default KformBuilderListController;
